import { z } from "zod";

/**
 * Привязка задания к странице, с которой оно извлечено
 * (`docs/scan-extraction-design.md`, решение 4 и раздел 8).
 *
 * ⚠️ **Живёт в корне `@edu/shared`, а не за подпутём с ответами:** ответов здесь
 * нет ни одного поля, а читают привязку и показываемая половина задания
 * (`ExerciseElement`), и комната второй волны, и сбор файлов заготовки
 * (`slidePageFileIds`). Барьер раздела 13 проекта.
 *
 * ⚠️ **Поле элемента называется `page`, а не `fileId`, и это замок, а не
 * вкус.** `slideFileIds` раздаёт ученику право на любой `fileId` элемента
 * (так доезжает SVG графика), а картинка страницы правом ученика не становится
 * (раздел 8: урок пишет только `slideFileIds` и `slideBoardImageIds`).
 */

/** Рамки в соглашении Gemini (решение 6): доли картинки целыми 0..1000. */
export const PAGE_BOX_MAX = 1000;

const BoxSide = z.number().int().min(0).max(PAGE_BOX_MAX);

/**
 * Рамка `[ymin, xmin, ymax, xmax]`, невырожденная: у пустой рамки нечего
 * вырезать, а кусок страницы нулевой высоты в панели свойств выглядел бы
 * поломкой, а не заданием.
 */
export const PageBox = z
  .tuple([BoxSide, BoxSide, BoxSide, BoxSide])
  .refine(([ymin, xmin, ymax, xmax]) => ymin < ymax && xmin < xmax);
export type PageBox = z.infer<typeof PageBox>;

/**
 * Файл страницы, его размер и рамка задания на нём.
 *
 * `width` и `height` nullable, как у `SlideBackground`: у скана их знает
 * отрисовка чтения, у старого пути подложка слайда, а у привязки, пришедшей
 * без них, размер берётся с картинки при показе. `box: null` у задания без
 * рамки (старый путь и «Собрать задание вручную»): в панели тогда страница
 * целиком уменьшенной.
 *
 * `backing` это файл ПОДЛОЖКИ слайда страницы, с которого задание поставлено
 * (решение 12): у скана привязка смотрит в отрисовку чтения, и подложку
 * разбора иначе знает только последний разбор заготовки. По нему редактор
 * решает, стоит ли страница в слайдах и нужна ли кнопка «Вернуть страницу в
 * заготовку». У старого пути `backing` совпадает с `fileId`. Поле
 * необязательное: у привязок до него кнопки возврата нет, а не ложной кнопки.
 *
 * ⚠️ Схема `z.object`, а не строгая: вкладка, которая `backing` не знает,
 * задание с ним не отвергает, а молча снимает поле при своём сохранении, и
 * кнопка возврата у такого задания пропадает (безопасная сторона).
 * Поле называется не `fileId` по той же причине, что и `page` (шапка файла):
 * подложка правом ученика через элемент не становится.
 */
export const PageLink = z.object({
  fileId: z.string().uuid(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  box: PageBox.nullable(),
  backing: z.string().uuid().optional(),
});
export type PageLink = z.infer<typeof PageLink>;

/**
 * Привязка в ЭЛЕМЕНТЕ: та же схема, но мягко.
 *
 * ⚠️ **`.catch(undefined)` обязателен**, причина та же, что у `GraphLabelTex`:
 * элемент, не прошедший схему, выбрасывается целиком, и битая или будущая форма
 * привязки у старой вкладки сняла бы с доски всё задание. Здесь она становится
 * «привязки нет», а задание остаётся.
 */
export const SoftPageLink = PageLink.optional().catch(undefined);
